import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Alert,
  AlertService,
  AlertType,
  ModalComponent,
  ModalResult,
  PaginatorFetcher,
  PaginatorFilters,
  TableComponent,
  TableConfig
} from '@michelin/theme';
import { Store } from '@ngxs/store';
import { debounceTime, distinctUntilChanged, map } from 'rxjs';
import { TranslationState } from 'src/app/shared/state/TranslationState';
import { TranslationService } from 'src/app/shared/translations/services/translation.service';
import { Pagination } from '../../../customer/models/response.model';
import { UserManagementService } from '../../services/user-management.service';
import { DealerState } from '../../../../shared/state/DealerState';
import { AssignedCustomer, PaginatedAssignedCustomerResponse } from './model/paginated-assigned-customer.model';
import { UserStateService } from '../../services/user-state.service';
import { FormControl } from '@angular/forms';
import { canDeactivateForm } from '../../../../shared/utility/canDeactivateForm';
import { CanComponentDeactivate } from '../../../../shared/guards/unsaved-changes.guard';

@Component({
  selector: 'app-select-customer',
  templateUrl: './select-customer.component.html',
  styleUrls: ['./select-customer.component.scss'],
  standalone: false
})
export class SelectCustomerComponent implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {
  customers: AssignedCustomer[] = [];
  currentDealer: string;
  userId: string;
  pagination: Pagination;
  pageSizeOptions = [1, 5, 10, 50, 100];
  currentPage = 1;
  pageSize = 10;
  selectedCustomers: AssignedCustomer[] = [];
  initialSelectedCustomers: any[] = [];
  searchControl: FormControl = new FormControl<string>('');
  customerTypeControl: FormControl = new FormControl<string>(null);
  assignmentStatusControl: FormControl = new FormControl<string>('');
  customerTypes: string[] = ['Customer', 'AssociateDealer', 'AssociateCustomer', 'DefaultCustomer', 'MichelinCustomer'];
  bypassCanDeactivate = false;
  mode: string;

  @ViewChild(TableComponent, { static: true }) tableRef: TableComponent<AssignedCustomer>;
  @ViewChild('warningModal') modal: ModalComponent;
  @ViewChild('warningModalContent') contentModal: TemplateRef<any>;

  @ViewChild('unsavedChangesModal', { static: true }) unsavedChangesModal: ModalComponent;
  @ViewChild('unsavedConfirmation') unsavedChangesContentModal: TemplateRef<any>;

  tableConfig: TableConfig<AssignedCustomer> = {
    paginator: {
      pageSize: this.pageSize,
      pageSizeOptions: this.pageSizeOptions,
      forceBottomAction: true,
      position: 'bottom'
    },
    onSelectionChange: (elms: AssignedCustomer[]) => {
      this.selectedCustomers = elms;
      this.userStateService.setAssignedCustomers(this.selectedCustomers);
    },
    selection: {
      columnKeyForCheckboxLabel: 'name',
      allLabel: 'All'
    },
    onColumnsChange: (columns: string[]) => console.log('Column display change', columns),
    columns: {}
  };

  public translationService = inject(TranslationService);
  protected userManagementService = inject(UserManagementService);
  public alertService = inject(AlertService);
  private readonly store = inject(Store);
  public router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly userStateService = inject(UserStateService);

  constructor() {}

  fetcher: PaginatorFetcher<AssignedCustomer> = (filters: PaginatorFilters) => {
    this.currentPage = filters.page;
    this.pageSize = filters.limit;
    let plantId = '';
    const locationType = this.userStateService.getUserForm().get('userInfo').get('selectedLocationType')?.value;
    if (locationType === 'PLANT') {
      const location = this.userStateService.getUserForm().get('userInfo').get('selectedLocation')?.value;
      plantId = this.store.selectSnapshot(DealerState.plants).find((plant) => {
        return location === `${plant.code}-${plant.name}`;
      })?.id;
    }
    return this.userManagementService
      .searchUserAssignToCustomer(
        this.currentDealer,
        this.searchControl.value ?? '',
        this.customerTypeControl.value ?? '',
        this.assignmentStatusControl.value ?? '',
        this.userId ?? '',
        plantId,
        filters.page,
        filters.limit
      )
      .pipe(
        map((assignedCustomerResponse: PaginatedAssignedCustomerResponse) => {
          if (assignedCustomerResponse != null && assignedCustomerResponse.error != null && assignedCustomerResponse.error.length > 0) {
            // TODO: change the dialog and content once the error dialog design is finalized
            this.alertService.showAlert({
              type: AlertType.strong,
              content: this.translationService.translate('tryAgainText')
            } as Alert);
            return;
          } else {
            this.customers = assignedCustomerResponse.data.assignedCustomerDTOs;

            // check if userstate selected customer is empty
            if (this.userStateService.getAssignedCustomers().length === 0) {
              this.customers
                .filter((cc) => cc.isAssigned === 'Yes')
                .forEach((customer) => {
                  // added this to make checkboxes selected by default
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  this.selectedCustomers.push({ ...customer, page: 1 });
                  this.initialSelectedCustomers.push({ ...customer, page: 1 });
                });
            } else {
              // loop customers and add only the userstate selected customer
              this.customers.forEach((customer) => {
                const isSelected = this.userStateService.getAssignedCustomers().some((cc) => cc.customerPk === customer.customerPk);
                if (isSelected) {
                  // Store the customer from the customers list
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  this.selectedCustomers.push({ ...customer, page: 1 });
                  this.initialSelectedCustomers.push({ ...customer, page: 1 });
                }
              });
            }

            // Sync selection with table after data loads
            if (this.tableRef) {
              this.tableRef.initCheckboxData(this.selectedCustomers);
            }
            this.pagination = assignedCustomerResponse.data.pagination;
            return {
              items: this.customers,
              count: this.pagination.totalRecords
            };
          }
        })
      );
  };
  handleStatusChange() {
    this.tableRef.patchExtraFilters({ assignmentStatus: this.assignmentStatusControl.value });
    const filters = this.tableRef.getFilters();
    this.fetcher(filters);
  }
  handleTypeChange() {
    this.tableRef.patchExtraFilters({ customerType: this.customerTypeControl.value });
    const filters = this.tableRef.getFilters();
    this.fetcher(filters);
  }
  resetFilters() {
    this.searchControl.reset();
    this.customerTypeControl.reset();
    this.assignmentStatusControl.reset();
    this.tableRef.patchExtraFilters({
      searchString: this.searchControl.value,
      customerType: this.customerTypeControl.value,
      assignmentStatus: this.assignmentStatusControl.value
    });
    this.fetcher(this.tableRef.getFilters());
  }

  ngAfterViewInit(): void {
    this.tableRef.initCheckboxData(this.selectedCustomers);
  }

  private isFormChanged(): boolean {
    return JSON.stringify(this.selectedCustomers) !== JSON.stringify(this.initialSelectedCustomers);
  }

  canDeactivate(): boolean | Promise<boolean> {
    let promise: boolean | Promise<boolean>;
    if (!this.bypassCanDeactivate) {
      promise = canDeactivateForm(
        this.isFormChanged.bind(this),
        this.unsavedChangesModal,
        this.unsavedChangesContentModal,
        this.translationService
      );
      return typeof promise !== 'boolean'
        ? promise?.then((result) => {
            if (result) {
              this.userStateService.resetSelectedCustomer();
              this.bypassCanDeactivate = false;
              return true;
            } else {
              this.bypassCanDeactivate = false;
              return false;
            }
          })
        : promise;
    } else {
      return true;
    }
  }

  ngOnInit(): void {
    this.searchControl.valueChanges.pipe(debounceTime(400), distinctUntilChanged()).subscribe((searchString: string) => {
      if (this.searchControl.value.length >= 3 || this.searchControl.value.length === 0) {
        this.tableRef.patchExtraFilters({ searchString });
      }
    });

    this.store.select(DealerState.currentDealer).subscribe((dealer) => {
      this.currentDealer = dealer.id;
    });
    this.activatedRoute.params.subscribe((params) => {
      this.userId = params['userId'] === 'newUser' ? '' : params['userId'];
    });
    // Store mode from query params
    this.activatedRoute.queryParams.subscribe((params) => {
      this.mode = params['mode'];
    });
    this.store.select(TranslationState.currentLanguage).subscribe(() => {
      this.tableConfig.columns = {
        code: {
          cell: (elm: AssignedCustomer) => elm.code,
          header: this.translationService.translate('customerCodeText'),
          sortable: false
        },
        name: {
          cell: (elm: AssignedCustomer) => elm.customerName,
          header: this.translationService.translate('customerNameText'),
          sortable: false
        },
        city: { cell: (elm: AssignedCustomer) => elm.city, header: this.translationService.translate('cityText') },
        state: {
          cell: (elm: AssignedCustomer) => elm.stateProvince,
          header: this.translationService.translate('customerStateProvinceText')
        },
        type: { cell: 'typeCell', header: this.translationService.translate('typeColumnText') },
        isAssigned: {
          cell: 'activeCell',
          header: this.translationService.translate('assignedToUserText'),
          sortable: true
        }
      };
    });
  }
  checkIfCustomersAreSelected() {
    const userInfo = this.userStateService.getUserForm().get('userInfo');
    const userId = this.userId || '';
    const navigateToUser = () => {
      const route = userId ? [`/home/setup/usersetup/user/${userId}`] : ['/home/setup/usersetup/user/'];
      const queryParams = this.mode === 'add' ? { queryParams: { mode: 'add' }, replaceUrl: true } : { replaceUrl: true };
      this.router.navigate(route, queryParams);
    };

    if (this.selectedCustomers.length === 0 && this.customers.length > 0) {
      userInfo.value.restrictCustomerAccess = false;
      this.discard();
    } else {
      this.bypassCanDeactivate = true;
      userInfo.value.restrictCustomerAccess = true;
      navigateToUser();
    }
  }

  onModalClosed(result: ModalResult) {
    if (result?.result) {
      this.initialSelectedCustomers = this.selectedCustomers;
      const userId = this.userId || '';
      const route = userId ? [`/home/setup/usersetup/user/${userId}`] : ['/home/setup/usersetup/user/'];
      const queryParams = this.mode === 'add' ? { queryParams: { mode: 'add' }, replaceUrl: true } : { replaceUrl: true };
      this.router.navigate(route, queryParams);
    }
  }

  discard() {
    this.modal.open({
      width: 693,
      title: this.translationService.translate('warningText'),
      content: this.contentModal,
      confirm: this.translationService.translate('yesText'),
      cancel: this.translationService.translate('noText'),
      id: 'discard'
    });
  }
  ngOnDestroy() {
    // Reset the user state if the user navigates away from the select customer page and next page is not user page
    if (!this.router.url.includes('home/setup/usersetup/user')) {
      this.userStateService.resetSelectedCustomer();
      this.userStateService.resetState();
    }
  }
}
